import {
  Modal as ChakraModal,
  ModalCloseButton as ChakraModalCloseButton,
  ModalContent as ChakraModalContent,
  ModalOverlay as ChakraModalOverlay,
} from '@chakra-ui/react'
import * as React from 'react'

import { BgColorProvider } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { getColorValue } from '~theme/utils'
import { getRadiusValue, TRadius } from '~ui/styleGuide/radiuses'
import { shadows } from '~ui/styleGuide/shadows'
import { getSpacingValue, TSpace } from '~ui/styleGuide/spaces'
import { useTheme } from '~ui/theme/ThemeProvider'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

import { ModalSettingsProvider } from './context/ModalSettingsProvider'

type TTheme = ReturnType<typeof useTheme>

// different width variants of the modal
type TModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'full'

type TChakraProps = Pick<PropsOf<typeof ChakraModal>, 'finalFocusRef' | 'isOpen' | 'onClose' | 'id'>

type TProps = RequiredChildren &
  TChakraProps & {
    /**
     * background color of the whole modal
     * also sets the text color accordingly
     */
    bgColor?: TColorKey
    /**
     * whether the alternative closing should be allowed or not
     * this involves closing by pressing Esc on the keyboard or by clicking outside of the modal
     * @default true
     */
    allowAltClosing?: boolean
    /**
     * option to render the close icon in the top-right corner
     * the icon is positioned absolutely & might overlap some content
     * if you are using ModalHeader inside the modal, consider using this prop on the modal instead (to make it centered)
     */
    withCloseButton?: boolean
    /**
     * how far from the top & left should the close button be,
     * applies only with `withCloseButton: true`
     */
    closeButtonOffset?: PropsOf<typeof ChakraModalCloseButton>[`top`]
    /**
     * bgColor of the close button,
     * applies only with `withCloseButton: true`
     */
    closeButtonBgColor?: TColorKey
    /**
     * border radius of the whole Modal card
     * key of radius,
     * or an object with separate key for each corner
     */
    radius?: TRadius
    /**
     * spacing inside the modal and its sub-components
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
    /**
     * size variant of the modal
     * controls the max width of the content
     */
    size?: TModalSize
    /**
     * controls the overall height of the modal content including header, body and footer
     * @default `min-content``
     */
    height?: PropsOf<typeof ChakraModalContent>['height']
    /**
     * reduce the bottom padding on the header, vertical on the body and top on the footer
     * to half of the modal’s padding
     * useful mainly when the header is the same color as the rest of the modal
     * and you need a consistent spacing
     * @default false
     */
    compactPadding?: boolean
    /**
     * z-index of the modal's overlay to always overlap the content of the page
     */
    overlayZIndex?: PropsOf<typeof ChakraModalOverlay>['zIndex']
    /**
     * overflow of the modal content
     */
    contentOverflow?: PropsOf<typeof ChakraModalContent>['overflow']
  }

// https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/modal.ts
// replace base props that Chakra would normally provide via the theme
const getSizeProps = (size: TModalSize): Partial<PropsOf<typeof ChakraModalContent>> => {
  const marginVertical = `3.75rem`

  const baseSizeProps: Partial<PropsOf<typeof ChakraModalContent>> = {
    my: marginVertical,
    width: `90vw`,
  }

  switch (size) {
    case 'lg':
      return { ...baseSizeProps, maxW: '74rem' }

    case 'md':
      return { ...baseSizeProps, maxW: '56rem' }

    case 'sm':
      return { ...baseSizeProps, maxW: '35rem' }

    case 'xs':
      return { ...baseSizeProps, maxW: '24rem' }

    default:
      // case 'full':
      return { maxW: '100vw', minH: '100vh', my: 0 }
  }
}

/**
 * Base Modal content wrapper.
 * Use in combination with `useModal` controls & `ModalHeader`, `ModalBody` and `ModalFooter` components.
 */
export const Modal: React.FC<TProps> = ({
  children,
  size = `md`,
  height = `min-content`,
  radius = `medium`,
  padding = `medium`,
  compactPadding = false,
  allowAltClosing = true,
  withCloseButton = false,
  closeButtonOffset = `10px`,
  closeButtonBgColor,
  bgColor = `bgColors.primary`,
  overlayZIndex = 100,
  contentOverflow,
  ...chakraProps
}) => {
  const theme = useTheme()
  const radiusValue = getRadiusValue(radius)
  const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

  return (
    <BgColorProvider bgColor={bgColor}>
      {({ bgColorValue, fgColors }) => (
        <ChakraModal
          {...chakraProps}
          isCentered
          blockScrollOnMount
          scrollBehavior={`inside`}
          closeOnEsc={allowAltClosing}
          closeOnOverlayClick={allowAltClosing}
        >
          <ModalSettingsProvider padding={padding} radius={radius} compactPadding={compactPadding}>
            <ChakraModalOverlay bgColor={theme.specialColors.overlayHeavy} zIndex={overlayZIndex}>
              <ChakraModalContent
                {...getSizeProps(size)}
                {...themeDesignProps}
                height={height}
                marginX={`auto`} // center horizontally
                color={fgColors.normal}
                position={`relative`}
                bgColor={bgColorValue}
                borderRadius={radiusValue}
                overflow={contentOverflow}
              >
                {children}
                {withCloseButton && (
                  <ChakraModalCloseButton
                    cursor={`pointer`}
                    position={`absolute`}
                    top={closeButtonOffset}
                    right={closeButtonOffset}
                    {...getCloseBtnWithBgProps(theme, closeButtonBgColor)}
                  />
                )}
              </ChakraModalContent>
            </ChakraModalOverlay>
          </ModalSettingsProvider>
        </ChakraModal>
      )}
    </BgColorProvider>
  )
}

/**
 * When the close button gets bgColor, it also should have some other props according to the design system.
 */
const getCloseBtnWithBgProps = (theme: TTheme, closeButtonBgColor?: TColorKey) => {
  if (!closeButtonBgColor)
    return {
      bgColor: `transparent`,
      padding: 0,
    }

  const bgColorValue = getColorValue(theme)(closeButtonBgColor)
  return {
    bgColor: bgColorValue,
    shadow: shadows.outer[`bottom`],
    borderRadius: getRadiusValue(`small`),
    padding: getSpacingValue(`smallMedium`),
  }
}
